import { Link } from "react-router";
import { Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import { useDeleteMenuItemMutation } from "@/lib/mutations";
import { useMenuQuery } from "@/lib/queries";

export function MenuItems() {
  const { data, isPending, isError, error, refetch } = useMenuQuery();
  const deleteMenuItemMutation = useDeleteMenuItemMutation();
  const menu = data ?? [];

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`Delete "${name}"?`)) return;

    try {
      await deleteMenuItemMutation.mutateAsync(id);
      toast.success(`Deleted "${name}"`);
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    }
  };

  if (isPending) {
    return (
      <div className="flex h-48 items-center justify-center">
        <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-foreground border-t-transparent" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-48 flex-col items-center justify-center gap-3 rounded-lg border">
        <p className="text-[13px] text-muted-foreground">{getErrorMessage(error)}</p>
        <button
          onClick={() => refetch()}
          className="inline-flex h-8 items-center rounded-md border px-3 text-[12px] font-medium transition-colors hover:bg-muted"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-semibold tracking-tight">Menu</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">Manage your menu items.</p>
        </div>
        <Link
          to="/menu/new"
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-foreground px-3 text-[12px] font-medium text-background transition-opacity hover:opacity-90"
        >
          <Plus className="size-3.5" />
          Add item
        </Link>
      </div>

      {menu.length === 0 && (
        <div className="flex h-32 items-center justify-center rounded-lg border">
          <p className="text-[13px] text-muted-foreground">No menu items yet.</p>
        </div>
      )}

      {menu.map((category: any) => (
        <div key={category.id} className="space-y-3">
          <h2 className="text-[12px] font-medium uppercase tracking-wider text-muted-foreground">
            {category.name}
          </h2>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Name
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Price
                  </th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Status
                  </th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {category.items?.map((item: any) => (
                  <tr key={item.id} className="border-b last:border-0 transition-colors hover:bg-muted/20">
                    <td className="px-4 py-3">
                      <span className="text-[13px] font-medium">{item.name}</span>
                      {item.description && (
                        <p className="mt-0.5 text-[12px] text-muted-foreground line-clamp-1">
                          {item.description}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[13px] font-mono text-muted-foreground">
                      {item.price}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          item.available
                            ? "bg-foreground text-background"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {item.available ? "Available" : "Unavailable"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Link
                          to={`/menu/${item.id}/edit`}
                          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                          <Pencil className="size-3.5" />
                        </Link>
                        <button
                          onClick={() => handleDelete(item.id, item.name)}
                          disabled={deleteMenuItemMutation.isPending}
                          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {(!category.items || category.items.length === 0) && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-[13px] text-muted-foreground">
                      No items in this category.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
